import { isBrowser, keys } from "@mixitone/util";
import type { RealtimePostgresChangesFilter, RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import type { SupabaseClient } from "@supabase/supabase-js";
import Debug from "debug";
import {
  type AccountBroadcastHandler,
  type AccountBroadcastOperation,
  type AccountBroadcastPayload,
} from "./AccountBroadcastManager";
import {
  BROADCAST_COALESCE_MS,
  broadcastPayloadToPostgresChange,
  shouldIgnoreSelfBroadcast,
} from "./accountBroadcastDispatch";
import { isBroadcastCapableTable } from "./broadcastCapableTables";
import { getDeviceTokenFromAccessToken } from "./clientDeviceToken";
import { getClientOriginToken } from "./clientOriginToken";
import { SupabaseSubscriptionManager } from "./SupabaseSubscriptionManager";
import SupabaseDatabaseAdapter from "./adapters/SupabaseDatabaseAdapter";
import { oom } from "./index";

const debug = Debug("oom:SubscriptionManager");

export type SubscriptionSource = "server" | "client" | "sync";
type SubscriptionRemove = () => void;

export type SubscriptionCallback = <T extends { [index: string]: any }>(
  data: RealtimePostgresChangesPayload<T>[],
  source: SubscriptionSource,
) => void;

type BatchKey = `${string}-${SubscriptionSource}`;

export interface SubscriptionAddOptions {
  filter?: Record<string, any>;
  source?: SubscriptionSource | SubscriptionSource[];
  /** When true, skip account broadcasts that originated from this client (web tab or device). */
  ignoreSelfBroadcast?: boolean;
  callback: SubscriptionCallback;
}

let lastId = 1;
function nextId() {
  return String(lastId++);
}

function resolveSupabaseClient(): SupabaseClient | null {
  if (oom.config.supabaseClient) {
    return oom.config.supabaseClient;
  }
  const adapter = oom.config.adapter;
  if (adapter instanceof SupabaseDatabaseAdapter) {
    return adapter.supabase;
  }
  return null;
}

/**
 * Manages subscriptions to the realtime changes feed. This is a singleton class
 */
export class SubscriptionManager {
  private static _instance: SubscriptionManager | undefined;
  static get instance() {
    if (!this._instance) {
      this._instance = new SubscriptionManager();
    }
    return this._instance;
  }
  static add(tableName: string, options: SubscriptionAddOptions) {
    return this.instance.add(tableName, options);
  }

  static resetForTests() {
    this._instance?.destroy();
    this._instance = undefined;
  }

  // Map of keys (tableName:filterString) to Supabase filter objects
  private keyToFilter: Map<string, RealtimePostgresChangesFilter<any>> = new Map();
  // Map of keys to the set of subscription IDs using that key
  private keyToIds: Map<string, Set<string>> = new Map();
  // Map of subscription IDs to their corresponding key
  private idToKey: Map<string, string> = new Map();
  // Map of subscription IDs to their callback functions
  private callbacksById: Map<string, SubscriptionCallback> = new Map();
  // Map of subscription IDs to their source filters (Set for efficient checking)
  private sourceFiltersById: Map<string, Set<SubscriptionSource>> = new Map();
  private ignoreSelfBroadcastById: Map<string, boolean> = new Map();
  // Instance of the dedicated Supabase subscription manager
  private supabaseSubscriptionManager: SupabaseSubscriptionManager | undefined;
  private broadcastHandlerRemove: (() => void) | undefined;
  private authUnsubscribe: (() => void) | undefined;
  private clientDeviceToken: string | null = null;
  private broadcastCoalesceBuffers = new Map<
    string,
    Array<{ payload: AccountBroadcastPayload; event: AccountBroadcastOperation }>
  >();
  private broadcastCoalesceTimers = new Map<string, ReturnType<typeof setTimeout>>();

  private _batchMode: boolean = false;
  get batchMode() {
    return this._batchMode;
  }
  set batchMode(value: boolean) {
    try {
      if (this._batchMode && !value) {
        this.clearBatches();
      }
    } finally {
      this._batchMode = value;
    }
  }

  static async startBatch<R>(callback: () => Promise<R>): Promise<R> {
    return this.instance.startBatch(callback);
  }

  async startBatch<R>(callback: () => Promise<R>): Promise<R> {
    this.batchMode = true;
    try {
      return await callback();
    } finally {
      this.batchMode = false;
    }
  }

  constructor() {
    const supabase = resolveSupabaseClient();
    if (supabase) {
      this.supabaseSubscriptionManager = new SupabaseSubscriptionManager(
        supabase,
        this.receive.bind(this), // Pass the receive method as the callback
      );
      void this.wireDeviceTokenTracking(supabase);
      this.wireAccountBroadcast();
    } else {
      debug("Supabase client not initialized. Supabase subscriptions disabled.");
    }

    if (isBrowser()) {
      window.addEventListener("beforeunload", () => {
        this.destroy();
      });
    }
  }

  private usesBroadcastForTable(tableName: string) {
    if (oom.config.realtimeMode !== "hybrid") {
      return false;
    }
    if (!resolveSupabaseClient()) {
      return false;
    }
    return isBroadcastCapableTable(tableName);
  }

  private async wireDeviceTokenTracking(supabase: SupabaseClient) {
    const updateDeviceToken = async () => {
      const { data } = await supabase.auth.getSession();
      this.clientDeviceToken = getDeviceTokenFromAccessToken(data.session?.access_token);
    };

    await updateDeviceToken();
    const { data } = supabase.auth.onAuthStateChange(async () => {
      await updateDeviceToken();
    });
    this.authUnsubscribe = () => data.subscription.unsubscribe();
  }

  private wireAccountBroadcast() {
    const manager = oom.accountBroadcast;
    if (!manager) {
      return;
    }

    const handler: AccountBroadcastHandler = (payload, event) => {
      this.handleAccountBroadcast(payload, event);
    };
    this.broadcastHandlerRemove = manager.addHandler(handler);
  }

  private handleAccountBroadcast(payload: AccountBroadcastPayload, event: AccountBroadcastOperation) {
    const tableName = payload.table_name;
    if (!this.usesBroadcastForTable(tableName)) {
      debug(`Ignoring broadcast for table not routed via broadcast: ${tableName}`, payload.row_id);
      return;
    }

    let buffer = this.broadcastCoalesceBuffers.get(tableName);
    if (!buffer) {
      buffer = [];
      this.broadcastCoalesceBuffers.set(tableName, buffer);
    }
    buffer.push({ payload, event });
    debug("broadcast buffered", tableName, payload.row_id, { event, buffer_size: buffer.length });
    this.scheduleBroadcastCoalesce(tableName);
  }

  private scheduleBroadcastCoalesce(tableName: string) {
    const existing = this.broadcastCoalesceTimers.get(tableName);
    if (existing) {
      clearTimeout(existing);
    }

    const timer = setTimeout(() => {
      this.broadcastCoalesceTimers.delete(tableName);
      this.flushBroadcastBuffer(tableName);
    }, BROADCAST_COALESCE_MS);
    this.broadcastCoalesceTimers.set(tableName, timer);
  }

  private flushBroadcastBuffer(tableName: string) {
    const buffered = this.broadcastCoalesceBuffers.get(tableName);
    if (!buffered?.length) {
      this.broadcastCoalesceBuffers.delete(tableName);
      return;
    }

    this.broadcastCoalesceBuffers.delete(tableName);

    const keys = [...this.keyToIds.keys()].filter((key) => key.startsWith(`${tableName}:`));
    debug(`Flushing ${buffered.length} broadcast event(s) for ${tableName}`, {
      subscription_keys: keys,
      row_ids: buffered.map(({ payload }) => payload.row_id),
    });

    const clientOriginToken = getClientOriginToken();
    for (const key of keys) {
      const ids = this.keyToIds.get(key);
      if (!ids) continue;

      for (const id of ids) {
        const ignoreSelf = this.ignoreSelfBroadcastById.get(id) ?? false;
        const filtered = ignoreSelf
          ? buffered.filter(
              ({ payload }) => !shouldIgnoreSelfBroadcast(clientOriginToken, this.clientDeviceToken, payload),
            )
          : buffered;

        if (filtered.length === 0) continue;

        const payloads = filtered.map(({ payload, event }) =>
          broadcastPayloadToPostgresChange(payload, event),
        );
        this.dispatchToSubscriber(id, payloads, "server");
      }
    }
  }

  private callbackName(tableName: string, filters: string) {
    return `${tableName}:${filters}`;
  }

  private remove(id: string) {
    const key = this.idToKey.get(id);
    if (!key) return;

    const sourceFilter = this.sourceFiltersById.get(id);
    const hadServerSource = sourceFilter?.has("server");
    const tableName = key.split(":")[0] ?? "";

    this.idToKey.delete(id);
    this.callbacksById.delete(id);
    this.sourceFiltersById.delete(id);
    this.ignoreSelfBroadcastById.delete(id);

    const ids = this.keyToIds.get(key);
    if (ids) {
      ids.delete(id);
      if (ids.size === 0) {
        // If there are no more callbacks for this key, remove the key mapping
        this.keyToIds.delete(key);
        const filter = this.keyToFilter.get(key);
        this.keyToFilter.delete(key);

        // Broadcast tables never register postgres_changes listeners.
        if (
          hadServerSource &&
          filter &&
          this.supabaseSubscriptionManager &&
          !this.usesBroadcastForTable(tableName)
        ) {
          debug(`Removing Supabase subscription for key: ${key}`);
          this.supabaseSubscriptionManager.removeSubscription(key);
        }
      }
    }
  }

  /**
   * Adds a subscription callback for changes matching the table and filter.
   * Optionally specifies the sources the callback is interested in.
   * Only creates a Supabase subscription if the source includes "server".
   *
   * @param tableName The name of the table to subscribe to.
   * @param filter An object defining filter conditions (currently supports one key-value pair).
   * @param callback The function to call when a matching change occurs.
   * @param source The source(s) of changes the callback should react to ('server', 'client', 'sync'). Defaults to all if undefined.
   * @returns A function to remove the subscription.
   */
  async add(tableName: string, options: SubscriptionAddOptions): Promise<SubscriptionRemove> {
    const { filter = {}, callback, source, ignoreSelfBroadcast = false } = options;

    const id = nextId();
    const removeFn = () => this.remove(id); // Renamed for clarity
    this.callbacksById.set(id, callback);

    const sourcesSet = new Set(Array.isArray(source) ? source : source ? [source] : []);
    if (sourcesSet.size > 0) {
      this.sourceFiltersById.set(id, sourcesSet);
    }
    if (ignoreSelfBroadcast) {
      this.ignoreSelfBroadcastById.set(id, true);
    }
    const wantsServerUpdates = !source || sourcesSet.has("server"); // Listen to server if no source specified or 'server' is included

    if (Object.keys(filter).length > 1) {
      // Consider supporting multiple filters in the future if needed
      console.warn(
        "oom:SubscriptionManager currently only supports one filter property per subscription. Using the first one.",
      );
      // Or throw new Error("Only one filter is supported at this time");
    }

    // Generate the filter string for the key (consistent format)
    const channelFilter = keys(filter).reduce((acc, k) => {
      acc = acc ? `${acc},` : "";
      const value = filter[k];
      if (value instanceof Array) {
        // Ensure consistent sorting for array values to generate the same key
        const sortedValues = [...value].sort();
        return `${acc}${k}=in.(${sortedValues.map((v) => `'${v}'`).join(",")})`;
      } else {
        return `${acc}${k}=eq.${value}`;
      }
    }, "");

    const key = this.callbackName(tableName, channelFilter);
    this.idToKey.set(id, key);

    let realtimeFilter = this.keyToFilter.get(key);

    // If this key doesn't exist yet, create the filter object
    if (!realtimeFilter) {
      realtimeFilter = {
        event: "*",
        schema: "public", // Assuming 'public' schema, adjust if necessary
        table: tableName,
      };
      if (channelFilter !== "") {
        realtimeFilter["filter"] = channelFilter;
      }
      this.keyToFilter.set(key, realtimeFilter);
      this.keyToIds.set(key, new Set()); // Initialize the set for this new key
    }

    // Add the current subscription ID to the set for this key
    this.keyToIds.get(key)?.add(id);

    // Logged tables in hybrid mode use account broadcast; others use postgres_changes.
    const viaBroadcast = this.usesBroadcastForTable(tableName);
    debug("subscription add", tableName, {
      key,
      via: viaBroadcast ? "broadcast" : wantsServerUpdates ? "postgres_changes" : "local_only",
      filter: channelFilter || null,
    });

    if (wantsServerUpdates && this.supabaseSubscriptionManager && realtimeFilter && !viaBroadcast) {
      debug(`Adding/Ensuring Supabase subscription for key: ${key}`);
      // SupabaseSubscriptionManager handles duplicates internally
      this.supabaseSubscriptionManager.addSubscription(key, realtimeFilter);
    }

    return removeFn;
  }

  /**
   * Emit a change event locally to relevant subscribers.
   * This is typically called after a local data modification.
   *
   * @param tableName The table associated with the change.
   * @param type The type of change ('INSERT', 'UPDATE', 'DELETE').
   * @param newData The new state of the data.
   * @param oldData The previous state of the data (optional, mainly for 'UPDATE'/'DELETE').
   * @param commit_timestamp An optional timestamp.
   * @param source The source of the emission (defaults to 'client').
   */
  emit(
    tableName: string,
    type: "INSERT" | "UPDATE" | "DELETE",
    newData: object,
    oldData: object = {},
    commit_timestamp: string = new Date().toISOString(), // Provide a default timestamp
    source: SubscriptionSource = "client",
  ) {
    // Iterate through all known keys
    for (const key of this.keyToIds.keys()) {
      // Check if the key starts with the relevant table name
      // This is a basic check; more sophisticated filtering might be needed if keys become complex
      if (key.startsWith(tableName + ":")) {
        // Construct a payload similar to what Supabase sends
        const payload: RealtimePostgresChangesPayload<any> = {
          eventType: type,
          new: newData,
          schema: "public", // Assuming 'public' schema
          table: tableName,
          commit_timestamp,
          errors: [], // Assuming no errors for local emits
          old: oldData,
        };
        // Call the internal receive method to distribute the event
        this.receive(key, payload, source);
      }
    }
  }

  private batches: Map<BatchKey, RealtimePostgresChangesPayload<any>[]> = new Map();
  private clearBatches() {
    try {
      for (const batch of this.batches) {
        const batchKey = batch[0];
        const dashRIndex = batchKey.lastIndexOf("-");
        const key = batchKey.substring(0, dashRIndex);
        const source = batchKey.substring(dashRIndex + 1) as SubscriptionSource;
        const payloads = batch[1];

        const ids = this.keyToIds.get(key) ?? new Set();
        for (const id of ids) {
          const callback = this.callbacksById.get(id);
          const sourceFilter = this.sourceFiltersById.get(id);

          if (!callback) continue;
          if (sourceFilter && sourceFilter.size > 0 && !sourceFilter.has(source)) continue;

          try {
            void Promise.resolve(callback(payloads, source)).catch((error) => {
              console.error(`Error in subscription callback for id ${id}, key ${key}:`, error);
              debug(`Error in callback for id ${id}`, error);
            });
          } catch (error) {
            console.error(`Error in subscription callback for id ${id}, key ${key}:`, error);
            debug(`Error in callback for id ${id}`, error);
          }
        }
      }
    } finally {
      this.batches = new Map();
    }
  }

  /**
   * Internal method to distribute received payloads (from Supabase or local emit)
   * to the appropriate callbacks based on their key and source filters.
   *
   * @param key The subscription key (tableName:filterString).
   * @param payload The data payload of the change event.
   * @param source The source of the event ('server', 'client', 'sync').
   */
  private receive(
    key: string,
    payload: RealtimePostgresChangesPayload<any>,
    source: SubscriptionSource = "server", // Default to 'server' if coming from Supabase manager
  ) {
    this.receiveToKey(key, [payload], source);
  }

  /**
   * Fan a batch of payloads out to every subscription on a key.
   * Broadcast events are not filterable at the payload layer; refetch filtering
   * happens in Query.subscribe via the query's own predicates.
   */
  private receiveToKey(
    key: string,
    payloads: RealtimePostgresChangesPayload<any>[],
    source: SubscriptionSource = "server",
  ) {
    debug("receive", key, {
      source,
      subscriber_count: this.keyToIds.get(key)?.size ?? 0,
      payloads,
    });

    if (payloads.length === 0) {
      return;
    }

    if (this.batchMode) {
      const batchKey: BatchKey = `${key}-${source}`;
      if (!this.batches.has(batchKey)) {
        this.batches.set(batchKey, []);
      }
      this.batches.get(batchKey)!.push(...payloads);
      return;
    }

    const ids = this.keyToIds.get(key);
    if (!ids) {
      debug(`No IDs found for key ${key}. Ignoring received event.`);
      return; // No subscriptions for this specific key
    }

    for (const id of ids) {
      this.dispatchToSubscriber(id, payloads, source);
    }
  }

  private dispatchToSubscriber(
    id: string,
    payloads: RealtimePostgresChangesPayload<any>[],
    source: SubscriptionSource,
  ) {
    const callback = this.callbacksById.get(id);
    const sourceFilter = this.sourceFiltersById.get(id);
    const key = this.idToKey.get(id);

    if (!callback) return;
    if (sourceFilter && sourceFilter.size > 0 && !sourceFilter.has(source)) return;

    if (this.batchMode && key) {
      const batchKey: BatchKey = `${key}-${source}`;
      if (!this.batches.has(batchKey)) {
        this.batches.set(batchKey, []);
      }
      this.batches.get(batchKey)!.push(...payloads);
      return;
    }

    try {
      void Promise.resolve(callback(payloads, source)).catch((error) => {
        console.error(`Error in subscription callback for id ${id}, key ${key}:`, error);
        debug(`Error in callback for id ${id}`, error);
      });
    } catch (error) {
      console.error(`Error in subscription callback for id ${id}, key ${key}:`, error);
      debug(`Error in callback for id ${id}`, error);
    }
  }

  private destroy() {
    for (const timer of this.broadcastCoalesceTimers.values()) {
      clearTimeout(timer);
    }
    this.broadcastCoalesceTimers.clear();
    this.broadcastCoalesceBuffers.clear();
    this.broadcastHandlerRemove?.();
    this.broadcastHandlerRemove = undefined;
    this.authUnsubscribe?.();
    this.authUnsubscribe = undefined;
    void this.supabaseSubscriptionManager?.unsubscribe();
  }
}

// Expose for debugging or specific use cases if needed
// @ts-ignore
globalThis.SubscriptionManager = SubscriptionManager;
